import { apiRequest } from '../lib/api-client';

/** Events, availability and booking links (context.md sections 116-124). */

export type EventStatus = 'CONFIRMED' | 'TENTATIVE' | 'CANCELED';
export type EventVisibility = 'DEFAULT' | 'PUBLIC' | 'PRIVATE';
export type EventTransparency = 'OPAQUE' | 'TRANSPARENT';
export type CalendarAccess = 'FREE_BUSY' | 'READ' | 'WRITE' | 'MANAGE';
export type CalendarKind = 'PERSONAL' | 'TEAM' | 'SHARED' | 'RESOURCE';
export type Frequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

/** Which occurrences a change to a series applies to. */
export type SeriesScope = 'THIS' | 'FOLLOWING' | 'ALL';

export interface RuleSummary {
  frequency: Frequency;
  interval: number;
  weekdays: number[];
  ends: 'NEVER' | 'AFTER' | 'ON';
  count: number | null;
  until: string | null;
}

export interface RuleInput {
  frequency: Frequency;
  interval?: number;
  weekdays?: number[];
  count?: number;
  until?: string;
}

export interface CalendarView {
  id: string;
  name: string;
  description: string | null;
  kind: CalendarKind;
  color: string;
  ownerId: string | null;
  teamId: string | null;
  timezone: string;
  defaultReminders: number[];
  isDefault: boolean;
  access: CalendarAccess;
  hidden: boolean;
}

export interface EventView {
  id: string;
  seriesId: string | null;
  occurrenceStart: string | null;
  calendarId: string | null;
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  timezone: string;
  status: EventStatus;
  color: string | null;
  visibility: EventVisibility;
  transparency: EventTransparency;
  guestsCanInvite: boolean;
  guestsCanModify: boolean;
  guestsCanSeeOthers: boolean;
  conferenceUrl: string | null;
  organizerId: string | null;
  ownerId: string | null;
  relatedType: string | null;
  relatedId: string | null;
  recurrence: RuleSummary | null;
  /** Drawn from a rule rather than read from a row. */
  virtual: boolean;
  canEdit: boolean;
  canRemove: boolean;
}

/**
 * What somebody sees of an event on a calendar they may only see the shape of.
 *
 * A separate type on purpose, matching the server: there is nothing in it to leak, and the screen
 * cannot accidentally render a title that was never sent.
 */
export interface BusyBlock {
  id: string;
  calendarId: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  busy: true;
}

export type WindowItem = EventView | BusyBlock;

export function isBusy(item: WindowItem): item is BusyBlock {
  return (item as BusyBlock).busy === true;
}

export interface AttendeeRow {
  id: string;
  userId: string | null;
  email: string | null;
  name: string | null;
  response: 'NEEDS_ACTION' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE';
  isOrganizer: boolean;
  optional: boolean;
  comment: string | null;
  proposedStartsAt: string | null;
  proposedEndsAt: string | null;
}

export interface EventDetail extends EventView {
  attendees: AttendeeRow[];
  attachments: { fileId: string; filename: string; sizeBytes: number; mimeType: string }[];
  canInvite: boolean;
}

export interface EventInput {
  title?: string;
  description?: string | null;
  location?: string | null;
  startsAt?: string;
  endsAt?: string;
  allDay?: boolean;
  timezone?: string;
  status?: EventStatus;
  calendarId?: string;
  color?: string | null;
  visibility?: EventVisibility;
  transparency?: EventTransparency;
  guestsCanInvite?: boolean;
  guestsCanModify?: boolean;
  guestsCanSeeOthers?: boolean;
  conferenceUrl?: string | null;
  recurrence?: RuleInput | null;
  attendees?: { userId?: string; email?: string; name?: string; optional?: boolean }[];
  reminders?: number[];
  ownerId?: string | null;
  /** Editing part of a series needs both of these. */
  scope?: SeriesScope;
  occurrenceStart?: string;
}

export function listWindow(range: {
  from: string;
  to: string;
  calendarIds?: string[];
}): Promise<{ items: WindowItem[] }> {
  const params = new URLSearchParams({ from: range.from, to: range.to, limit: '1000' });

  for (const id of range.calendarIds ?? []) params.append('calendarId', id);

  return apiRequest(`/v1/calendar/events?${params.toString()}`);
}

export function createEvent(input: EventInput & { title: string }): Promise<EventDetail> {
  return apiRequest('/v1/calendar/events', { method: 'POST', body: input });
}

export function getEvent(id: string): Promise<EventDetail> {
  return apiRequest(`/v1/calendar/events/${id}`);
}

export function updateEvent(id: string, changes: EventInput): Promise<EventDetail> {
  return apiRequest(`/v1/calendar/events/${id}`, { method: 'PATCH', body: changes });
}

export function deleteEvent(
  id: string,
  options: { scope?: SeriesScope; occurrenceStart?: string } = {},
): Promise<void> {
  const params = new URLSearchParams();

  if (options.scope) params.set('scope', options.scope);
  if (options.occurrenceStart) params.set('occurrenceStart', options.occurrenceStart);

  const query = params.toString();

  return apiRequest(`/v1/calendar/events/${id}${query ? `?${query}` : ''}`, { method: 'DELETE' });
}

/**
 * "Me lembra de novo em dez minutos" (Phase 125).
 *
 * The person's own alert about the meeting, not the meeting's reminder: three people invited to the
 * same thing each postpone their own.
 */
export function snoozeEvent(id: string, minutes: number): Promise<{ remindAt: string }> {
  return apiRequest(`/v1/calendar/events/${id}/snooze`, { method: 'POST', body: { minutes } });
}

export function respondToEvent(
  id: string,
  body: {
    response: AttendeeRow['response'];
    comment?: string | null;
    proposedStartsAt?: string;
    proposedEndsAt?: string;
  },
): Promise<EventDetail> {
  return apiRequest(`/v1/calendar/events/${id}/response`, { method: 'POST', body });
}

export function inviteGuests(
  id: string,
  guests: { userId?: string; email?: string; name?: string; optional?: boolean }[],
): Promise<EventDetail> {
  return apiRequest(`/v1/calendar/events/${id}/guests`, { method: 'POST', body: { guests } });
}

// ---------------------------------------------------------------------------
// Calendars
// ---------------------------------------------------------------------------

export function listCalendars(): Promise<{ calendars: CalendarView[] }> {
  return apiRequest('/v1/calendar/calendars');
}

export function createCalendar(input: {
  name: string;
  kind?: CalendarKind;
  color?: string;
  teamId?: string | null;
  timezone?: string;
}): Promise<CalendarView> {
  return apiRequest('/v1/calendar/calendars', { method: 'POST', body: input });
}

export function updateCalendar(
  id: string,
  patch: { name?: string; color?: string; timezone?: string; active?: boolean },
): Promise<CalendarView> {
  return apiRequest(`/v1/calendar/calendars/${id}`, { method: 'PATCH', body: patch });
}

export function deleteCalendar(id: string): Promise<void> {
  return apiRequest(`/v1/calendar/calendars/${id}`, { method: 'DELETE' });
}

/** Showing, hiding and recolouring — for this person only. */
export function setCalendarPreference(
  id: string,
  preference: { hidden?: boolean; color?: string | null },
): Promise<void> {
  return apiRequest(`/v1/calendar/calendars/${id}/preference`, {
    method: 'PUT',
    body: preference,
  });
}

export interface ShareView {
  userId: string;
  access: CalendarAccess;
  createdAt: string;
}

export function listShares(id: string): Promise<{ shares: ShareView[] }> {
  return apiRequest(`/v1/calendar/calendars/${id}/shares`);
}

export function shareCalendar(id: string, userId: string, access: CalendarAccess): Promise<void> {
  return apiRequest(`/v1/calendar/calendars/${id}/shares/${userId}`, {
    method: 'PUT',
    body: { access },
  });
}

export function unshareCalendar(id: string, userId: string): Promise<void> {
  return apiRequest(`/v1/calendar/calendars/${id}/shares/${userId}`, { method: 'DELETE' });
}

export function freeBusy(input: {
  userIds: string[];
  from: string;
  to: string;
}): Promise<{ people: { userId: string; busy: { startsAt: string; endsAt: string }[] }[] }> {
  const params = new URLSearchParams({ from: input.from, to: input.to });

  for (const id of input.userIds) params.append('userId', id);

  return apiRequest(`/v1/calendar/free-busy?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Availability

export interface WeeklyRule {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

export interface ScheduleRow {
  id: string;
  name: string;
  timezone: string;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_minutes: number;
  max_days_ahead: number;
}

export interface DateOverrideRow {
  date: string;
  available: boolean;
  startMinute: number | null;
  endMinute: number | null;
}

export interface ScheduleView {
  schedule: ScheduleRow | null;
  rules: WeeklyRule[];
  overrides: DateOverrideRow[];
}

export function getSchedule(): Promise<ScheduleView> {
  return apiRequest('/v1/calendar/availability');
}

export interface SchedulePayload {
  name?: string;
  timezone?: string;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  minNoticeMinutes?: number;
  maxDaysAhead?: number;
  rules: WeeklyRule[];
}

export function saveSchedule(payload: SchedulePayload): Promise<ScheduleRow> {
  return apiRequest('/v1/calendar/availability', { method: 'PUT', body: payload });
}

export function blockDate(input: {
  date: string;
  available?: boolean;
  reason?: string | null;
}): Promise<unknown> {
  return apiRequest('/v1/calendar/availability/overrides', { method: 'POST', body: input });
}

export interface BookingLinkRow {
  id: string;
  slug: string;
  name: string;
  duration_minutes: number;
  is_active: boolean;
}

export function listBookingLinks(): Promise<{ links: BookingLinkRow[] }> {
  return apiRequest('/v1/calendar/booking-links');
}

export function createBookingLink(input: {
  slug: string;
  name: string;
  description?: string | null;
  durationMinutes: number;
  location?: string | null;
}): Promise<BookingLinkRow> {
  return apiRequest('/v1/calendar/booking-links', { method: 'POST', body: input });
}

export interface BookingRow {
  id: string;
  link_id: string;
  invitee_name: string;
  invitee_email: string;
  starts_at: string;
  ends_at: string;
  status: string;
}

export function listBookings(): Promise<{ bookings: BookingRow[] }> {
  return apiRequest('/v1/calendar/bookings?limit=100');
}

/**
 * The agenda: everything dated, in one window (Phase 36).
 *
 * Events, tasks that fall due and the two ends of a sprint arrive together, each already filtered
 * by what the caller may see. The screen overlays the non-event kinds onto the month grid rather
 * than replacing its own event query, so an event keeps its colour and its editor.
 */
export type AgendaKind = 'EVENT' | 'TASK' | 'SPRINT_START' | 'SPRINT_END';

export interface AgendaItem {
  id: string;
  kind: AgendaKind;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  href: string;
  detail?: {
    key: string | null;
    status: string;
    priority: string;
    assigneeId: string | null;
  } | null;
}

export function getAgenda(range: { from: string; to: string }): Promise<{ items: AgendaItem[] }> {
  const params = new URLSearchParams({ from: range.from, to: range.to });

  return apiRequest(`/v1/calendar/agenda?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Colours, proposals and external calendars (Phase 52)
// ---------------------------------------------------------------------------

export interface EventColor {
  key: string;
  hex: string;
}

export function listColors(): Promise<{ colors: EventColor[] }> {
  return apiRequest('/v1/calendar/colors');
}

/** The organiser takes a guest's proposed time. It moves the event and tells everybody. */
export function acceptProposal(eventId: string, attendeeId: string): Promise<EventDetail> {
  return apiRequest(`/v1/calendar/events/${eventId}/proposals/${attendeeId}/accept`, {
    method: 'POST',
  });
}

export type SyncDirection = 'PULL' | 'PUSH' | 'BOTH';
export type SyncStatus = 'PENDING' | 'ACTIVE' | 'ERROR' | 'REVOKED';

export interface ConnectionView {
  id: string;
  provider: string;
  accountEmail: string | null;
  externalCalendarId: string | null;
  calendarId: string;
  direction: SyncDirection;
  status: SyncStatus;
  lastSyncedAt: string | null;
  /** A key, never a provider's own sentence. The browser translates it. */
  lastError: string | null;
  createdAt: string;
}

export function listConnections(): Promise<{ connections: ConnectionView[] }> {
  return apiRequest('/v1/calendar/connections');
}

export function connectCalendar(input: {
  provider: 'google';
  calendarId: string;
  direction?: SyncDirection;
}): Promise<ConnectionView> {
  return apiRequest('/v1/calendar/connections', { method: 'POST', body: input });
}

export function setSyncDirection(id: string, direction: SyncDirection): Promise<ConnectionView> {
  return apiRequest(`/v1/calendar/connections/${id}`, { method: 'PATCH', body: { direction } });
}

export function disconnectCalendar(id: string): Promise<void> {
  return apiRequest(`/v1/calendar/connections/${id}`, { method: 'DELETE' });
}
